import { useState } from "react"
import { Loader2, Search } from "lucide-react"
import type { Complaint } from "../../shared/domain"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { NativeSelect } from "@/components/ui/native-select"
import { Textarea } from "@/components/ui/textarea"
import { apiErrorMessage, type ErpClient } from "@/lib/api"
import { useApp } from "@/lib/app-context"

/**
 * Registo interno (reclamação recebida por telefone, email ou presencialmente).
 * O cliente procura-se pelo n.º ERP (+ estabelecimento); escolhido, a
 * reclamação fica ligada à entidade e como verificada.
 */
export function NewComplaintDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean
  onOpenChange(v: boolean): void
  onCreated(c: Complaint): void
}) {
  const { api, settings, users, me, toast } = useApp()
  const [channel, setChannel] = useState<"interno" | "email">("interno")
  const [number, setNumber] = useState("")
  const [estab, setEstab] = useState("0")
  const [found, setFound] = useState<ErpClient | null>(null)
  const [searching, setSearching] = useState(false)
  const [lookupMsg, setLookupMsg] = useState<string | null>(null)
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [phone, setPhone] = useState("")
  const [category, setCategory] = useState("")
  const [severity, setSeverity] = useState("")
  const [owner, setOwner] = useState(me ?? "")
  const [description, setDescription] = useState("")
  const [expectation, setExpectation] = useState("")
  const [invoice, setInvoice] = useState("")
  const [saving, setSaving] = useState(false)

  const reset = () => {
    setNumber("")
    setEstab("0")
    setFound(null)
    setLookupMsg(null)
    setName("")
    setEmail("")
    setPhone("")
    setCategory("")
    setSeverity("")
    setDescription("")
    setExpectation("")
    setInvoice("")
  }

  const lookup = async () => {
    if (!number.trim()) return
    setSearching(true)
    setLookupMsg(null)
    try {
      const { items } = await api.lookupClient({ number, estab })
      const c = items[0] ?? null
      setFound(c)
      if (c) {
        setName(c.name)
        setEmail(c.email ?? "")
        setPhone(c.phone ?? "")
        if (!c.entity_id) setLookupMsg("Cliente sem entidade no CRM ainda — fica registado sem ligação.")
      } else {
        setLookupMsg("Nenhum cliente com essa referência. Podes registar na mesma com os dados de contacto.")
      }
    } catch (e) {
      setLookupMsg(apiErrorMessage(e))
    } finally {
      setSearching(false)
    }
  }

  const submit = async () => {
    setSaving(true)
    try {
      const c = await api.createComplaint({
        channel,
        description,
        expectation,
        category: category || null,
        severity: severity || null,
        owner_user_id: owner || null,
        entity_id: found?.entity_id ?? null,
        ext_client_id: found?.id ?? null,
        estab: found ? estab : null,
        submitted_name: name,
        submitted_email: email,
        submitted_phone: phone,
        invoice_ref: invoice,
      })
      toast("success", `Reclamação ${c.ref} registada`)
      reset()
      onCreated(c)
    } catch (e) {
      toast("error", "Não foi possível registar", apiErrorMessage(e))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Registar reclamação</DialogTitle>
        </DialogHeader>

        <div className="grid gap-4">
          <div className="grid gap-3 sm:grid-cols-[1fr_6rem_auto] sm:items-end">
            <div className="grid gap-1.5">
              <Label>N.º de cliente</Label>
              <Input value={number} onChange={(e) => setNumber(e.target.value)} onKeyDown={(e) => e.key === "Enter" && lookup()} />
            </div>
            <div className="grid gap-1.5">
              <Label>Estab.</Label>
              <Input value={estab} onChange={(e) => setEstab(e.target.value)} />
            </div>
            <Button type="button" variant="outline" onClick={lookup} disabled={searching || !number.trim()}>
              {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Procurar
            </Button>
          </div>
          {found ? (
            <p className="rounded-md bg-green-50 px-3 py-2 text-xs text-green-800">
              {found.name} · {found.external_id}
              {found.tax_id ? ` · NIF ${found.tax_id}` : ""}
            </p>
          ) : null}
          {lookupMsg ? <p className="text-xs text-muted-foreground">{lookupMsg}</p> : null}

          <div className="grid gap-3 sm:grid-cols-3">
            <div className="grid gap-1.5">
              <Label>Nome / empresa</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="grid gap-1.5">
              <Label>Email</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div className="grid gap-1.5">
              <Label>Telefone</Label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} />
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-4">
            <div className="grid gap-1.5">
              <Label>Canal</Label>
              <NativeSelect value={channel} onChange={(e) => setChannel(e.target.value as "interno" | "email")}>
                <option value="interno">Registo interno</option>
                <option value="email">Email</option>
              </NativeSelect>
            </div>
            <div className="grid gap-1.5">
              <Label>Motivo</Label>
              <NativeSelect value={category} onChange={(e) => setCategory(e.target.value)}>
                <option value="">—</option>
                {settings.categories.map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </NativeSelect>
            </div>
            <div className="grid gap-1.5">
              <Label>Gravidade</Label>
              <NativeSelect value={severity} onChange={(e) => setSeverity(e.target.value)}>
                <option value="">—</option>
                {settings.severities.map((s) => (
                  <option key={s.key} value={s.key}>
                    {s.label} ({s.sla_days} dias)
                  </option>
                ))}
              </NativeSelect>
            </div>
            <div className="grid gap-1.5">
              <Label>Responsável</Label>
              <NativeSelect value={owner} onChange={(e) => setOwner(e.target.value)}>
                <option value="">—</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </NativeSelect>
            </div>
          </div>

          <div className="grid gap-1.5">
            <Label>Descrição do cliente</Label>
            <Textarea rows={4} value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          <div className="grid gap-3 sm:grid-cols-[1fr_12rem]">
            <div className="grid gap-1.5">
              <Label>O que o cliente pretende</Label>
              <NativeSelect value={expectation} onChange={(e) => setExpectation(e.target.value)}>
                <option value="">—</option>
                {settings.resolutions.map((r) => (
                  <option key={r}>{r}</option>
                ))}
              </NativeSelect>
            </div>
            <div className="grid gap-1.5">
              <Label>Fatura / encomenda</Label>
              <Input value={invoice} onChange={(e) => setInvoice(e.target.value)} />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={submit} disabled={saving || !description.trim()}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Registar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
